import { makeAutoObservable } from 'mobx';
import { ProfessionalModel, UpdateProfileResponse, UpdateOnboardingCredentials } from '../../model/professional/professional-model';
import { SECTION_DASHBOARD, SECTION_CALENDAR, SECTION_RESERVATION, SECTION_AVAILABILITY, SECTION_SERVICES, SECTION_PROFILE } from '../../constants/onboarding-sections';
import professionalStore from '../../store/professional-store';
import { HTTP_STATUS_CODE } from '../../utils/UHttp';

// État des sections d'onboarding
export class OnboardingViewModel {
    private professionalModel: ProfessionalModel;
    static instance: OnboardingViewModel;
    
    // État des sections d'onboarding
    sectionStatuses: Record<string, boolean> = {
        [SECTION_DASHBOARD]: false,
        [SECTION_CALENDAR]: false,
        [SECTION_RESERVATION]: false,
        [SECTION_AVAILABILITY]: false,
        [SECTION_SERVICES]: false,
        [SECTION_PROFILE]: false
    };
    
    // Étapes actuelles par section
    sectionSteps: Record<string, number | null> = {
        [SECTION_DASHBOARD]: null,
        [SECTION_CALENDAR]: null,
        [SECTION_RESERVATION]: null,
        [SECTION_AVAILABILITY]: null,
        [SECTION_SERVICES]: null,
        [SECTION_PROFILE]: null
    };
    
    // État global d'onboarding
    globalOnboardingCompleted: boolean = false;
    
    // État de chargement du profil
    profileLoaded: boolean = false;
    
    constructor(professionalModel: ProfessionalModel) {
        this.professionalModel = professionalModel;
        makeAutoObservable(this);
        
        // Vérifier si le profil est chargé
        this.checkProfileLoaded();
    }
    
    // Vérifier si le profil est chargé
    checkProfileLoaded = (): void => {
        if (professionalStore.profileProfessional) {
            this.profileLoaded = true;
            this.initializeOnboardingStatus();
        }
    }
    
    // Initialiser le statut d'onboarding à partir du localStorage et du serveur
    initializeOnboardingStatus = (): void => {
        // Récupérer le statut global d'onboarding depuis le profil
        const globalOnboardingCompleted = professionalStore.profileProfessional?.onboarding || false;
        this.globalOnboardingCompleted = globalOnboardingCompleted;
        
        // Si le statut global d'onboarding est true sur le serveur mais que le localStorage est vide
        // (cas après vidage du cache), on initialise toutes les sections comme complétées
        if (globalOnboardingCompleted) {
            Object.keys(this.sectionStatuses).forEach(section => {
                // Si le localStorage est vide pour cette section, on le marque comme complété
                if (localStorage.getItem(`onboarding_${section}`) === null) {
                    this.sectionStatuses[section] = true;
                    localStorage.setItem(`onboarding_${section}`, 'true');
                }
            });
        }
        
        // Initialiser les statuts des sections à partir du localStorage
        Object.keys(this.sectionStatuses).forEach(section => {
            const sectionStatus = localStorage.getItem(`onboarding_${section}`);
            if (sectionStatus !== null) {
                this.sectionStatuses[section] = sectionStatus === 'true';
            }
        });
        
        // Initialiser les étapes des sections à partir du localStorage
        Object.keys(this.sectionSteps).forEach(section => {
            const stepValue = localStorage.getItem(`onboarding_step_${section}`);
            if (stepValue !== null) {
                this.sectionSteps[section] = parseInt(stepValue, 10);
            }
        });
        
        // Vérifier si toutes les sections sont complétées
        this.checkAllSectionsCompleted();
    }
    
    // Mettre à jour le statut d'onboarding pour une section
    updateSectionOnboarding = (section: string, completed: boolean): void => {
        if (section in this.sectionStatuses) {
            this.sectionStatuses[section] = completed;
            
            // Sauvegarder dans localStorage
            localStorage.setItem(`onboarding_${section}`, String(completed));
            
            // Vérifier si toutes les sections sont complétées
            this.checkAllSectionsCompleted();
            
            // Déclencher un événement pour notifier les autres composants
            window.dispatchEvent(new CustomEvent('sectionOnboardingChange', { 
                detail: { section, completed } 
            }));
        }
    }
    
    // Mettre à jour l'étape actuelle pour une section
    updateSectionStep = (section: string, step: number | null): void => {
        if (section in this.sectionSteps) {
            this.sectionSteps[section] = step;
            
            // Sauvegarder dans localStorage
            if (step === null) {
                localStorage.removeItem(`onboarding_step_${section}`);
            } else {
                localStorage.setItem(`onboarding_step_${section}`, step.toString());
            }
            
            // Déclencher un événement pour notifier les autres composants
            window.dispatchEvent(new CustomEvent('onboardingStepChange', { 
                detail: { section, step } 
            }));
        }
    }
    
    // Réinitialiser l'onboarding pour une section spécifique
    resetSectionOnboarding = (section: string): void => {
        if (section in this.sectionStatuses) {
            this.sectionStatuses[section] = false;
            localStorage.setItem(`onboarding_${section}`, 'false');
            
            // Mettre à jour l'état global si nécessaire
            this.setGlobalOnboarding(false);
            
            // Réinitialiser l'étape actuelle pour cette section
            this.updateSectionStep(section, null);
            
            // Déclencher un événement
            window.dispatchEvent(new CustomEvent('sectionOnboardingChange', { 
                detail: { section, completed: false, reset: true } 
            }));
        }
    }
    
    // Vérifier si toutes les sections sont complétées
    checkAllSectionsCompleted = (): boolean => {
        const allCompleted = Object.values(this.sectionStatuses).every(status => status === true);
        
        // Mettre à jour l'état global d'onboarding
        if (allCompleted !== this.globalOnboardingCompleted) {
           this.setGlobalOnboarding(allCompleted);
        }
        
        return allCompleted;
    }
    
    // Obtenir le statut d'onboarding pour une section
    getSectionOnboardingStatus = (section: string): boolean => {
        return section in this.sectionStatuses ? this.sectionStatuses[section] : false;
    }
    
    // Obtenir l'étape actuelle pour une section
    getCurrentOnboardingStep = (section: string): number | null => {
        return section in this.sectionSteps ? this.sectionSteps[section] : null;
    }
    
    // Mettre à jour l'état global d'onboarding
    setGlobalOnboarding = (completed: boolean): void => {
        this.globalOnboardingCompleted = completed;
        
        // Mettre à jour le statut sur le serveur
        if (completed) {
            this.updateOnboarding(true).catch(error => {
                console.error('Erreur lors de la mise à jour du statut d\'onboarding:', error);
            });
        } else {
            this.updateOnboarding(false).catch(error => {
                console.error('Erreur lors de la mise à jour du statut d\'onboarding:', error);
            });
        }
    }
    
    // Mettre à jour le statut d'onboarding sur le serveur
    updateOnboarding = async (status: boolean): Promise<UpdateProfileResponse> => {
        try {
            const credentials: UpdateOnboardingCredentials = { onboarding: status };
            const response = await this.professionalModel.handleUpdateOnboarding(credentials);
            if (response.status === HTTP_STATUS_CODE.OK) {
                professionalStore.setOnboarding(status);
            }
            return response;
        } catch (error) {
            console.error('Erreur lors de la mise à jour du statut d\'onboarding:', error);
            throw error;
        }
    }
}

// Hook pour utiliser le ViewModel
export const useOnboardingViewModel = () => {
    // Singleton instance
    if (!OnboardingViewModel.instance) {
        // Utiliser la méthode getInstance pour obtenir une instance de ProfessionalModel
        const professionalModel = ProfessionalModel.getInstance();
        OnboardingViewModel.instance = new OnboardingViewModel(professionalModel);
    }
    return OnboardingViewModel.instance;
};
